import { commandsStore } from "@/store/commandsStore";
import { headerBiddingStore } from "@/store/headerBiddingStore";
import { isPageDataCommand } from "@/ts/guards";
import type { AuctionPageDataModule } from "@/ts/module";
import type { PageMiscData } from "@/ts/prebid/types";
import type {
	Exactly,
	LotameData,
	MantisSurgePrebid,
	Ortb2,
	PageDataCommand,
} from "@/ts/types";
import { getPageTempl } from "@/utils/prebidDataHelper";

/**
 * Owns the page-data command -> ortb2 transform for auctions.
 *
 * Single writer to headerBiddingStore.auctionData: builds ortb2 in one shot and
 * writes it via addAuctionData. Nothing else writes auction ortb2, so there is
 * no second subscriber that can clobber this data.
 *
 * Surge is one field on the page-data command among several (project, templ,
 * mantis_context, lotame). It is delivered serverside on the command and folded
 * in here alongside its siblings.
 */
export class AuctionPageData
	implements Exactly<AuctionPageDataModule, AuctionPageData>
{
	name = "AuctionPageData" as const;

	async init(): Promise<void> {
		const { selectors } = commandsStore.getState();

		commandsStore.subscribe(selectors.selectPageDataCommand, (selectedCmd) =>
			handlePageDataCmd(selectedCmd),
		);
	}
}

const handlePageDataCmd = (selectedCmd?: PageDataCommand) => {
	if (selectedCmd && isPageDataCommand(selectedCmd)) {
		const { lotame, mantis, misc: pageMisc, surge } = selectedCmd.data;
		const ortb2: Ortb2 = {
			user: getUserData(lotame, mantis),
			site: getSiteData(mantis, pageMisc, surge?.prebid),
		};

		const auctionData = {
			ortb2,
			pageMisc,
		};
		const { actions } = headerBiddingStore.getState();
		// Each page-data command is expected to carry the full data set: the page
		// sends multiple commands as it discovers more data, and every command is
		// cumulative. We therefore replace rather than merge. If Nav ever sends
		// incremental deltas, this must become a deep merge into the existing
		// auctionData.ortb2 (reuse the per-level ortb2 merge in Prebid.configurePbjs
		// rather than inventing a second one).
		actions.addAuctionData(auctionData);
	}
};

/**
 * Function that returns ortb2 data that can be readily plugged into setConfig of pbjs object
 * Refer the below links that suggests the below structure for ortb2 requirement.
 * https://docs.prebid.org/features/firstPartyData.html#segments-and-taxonomy
 * https://bitbucket.org/trinitymirror-ondemand/withnail/src/19e43d67c4305d893857d3447c5bb5ae3dc3c9da/src/tags/js/prebid.js#lines-198
 * https://bitbucket.org/trinitymirror-ondemand/withnail/src/19e43d67c4305d893857d3447c5bb5ae3dc3c9da/src/tags/js/prebid.js#lines-415
 */
const getUserData = (lotameData?: LotameData[], mantisKeywords?: string) => {
	const lotame = {
		name: "lotame.com",
		ext: { segtax: 4 },
		segment: lotameData,
	};

	const keywords = mantisKeywords;

	return lotame.segment || keywords ? { data: [lotame], keywords } : undefined;
};

const getSiteData = (
	mantisKeywords?: string,
	pageMisc?: PageMiscData,
	surgePrebid?: MantisSurgePrebid,
) => {
	const segments = mantisKeywords
		? mantisKeywords.split(",").map((id) => ({ id }))
		: [];

	return {
		content: {
			data: [
				{
					name: "mantis",
					segment: segments,
				},
			],
		},
		ext: {
			data: {
				...(mantisKeywords && {
					mantis_context: segments.map((segment) => segment.id),
				}),
				site: pageMisc?.project || "navigator",
				templ: getPageTempl(pageMisc),
				...surgePrebid,
			},
		},
	};
};
